import type { TMembershipRole } from '@escloud/types';
import { UserPlus, MoreHorizontal, Trash2, Mail } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCurrentTenant } from '@/context/tenant-registry-context';
import { useInvitations, useCreateInvitation, useRevokeInvitation } from '@/hooks/use-invitations';
import { useMembers, useRemoveMember } from '@/hooks/use-members';

export function MembersPage() {
    const { tenantId } = useParams<{ tenantId: string }>();
    const { entry: currentTenant, isConfirmed } = useCurrentTenant();
    const { data: membersData, isLoading: membersLoading } = useMembers(tenantId);
    const { data: invitationsData, isLoading: invitationsLoading } = useInvitations(tenantId);
    const removeMember = useRemoveMember(tenantId!);
    const revokeInvitation = useRevokeInvitation(tenantId!);

    // Confirmed by a regional API, never by a token claim: a claim keeps a
    // demoted role for the rest of the session.
    const isAdmin = isConfirmed && (currentTenant?.role === 'admin' || currentTenant?.role === 'owner');

    const handleRemoveMember = (userId: string, userName: string) => {
        if (confirm(`Are you sure you want to remove ${userName} from the organization?`)) {
            removeMember.mutate(userId, {
                onSuccess: () => {
                    toast.success('Member removed successfully');
                },
                onError: () => {
                    toast.error('Failed to remove member');
                },
            });
        }
    };

    const handleRevokeInvitation = (invitationId: string) => {
        revokeInvitation.mutate(invitationId, {
            onSuccess: () => {
                toast.success('Invitation revoked');
            },
            onError: () => {
                toast.error('Failed to revoke invitation');
            },
        });
    };

    if (membersLoading) {
        return <MembersSkeleton />;
    }

    const members = membersData?.items ?? [];
    const invitations = invitationsData?.items ?? [];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Team Members</h1>
                    <p className="text-muted-foreground">Manage your organization members</p>
                </div>
                {isAdmin && <InviteMemberDialog tenantId={tenantId!} />}
            </div>

            <Tabs defaultValue="members">
                <TabsList>
                    <TabsTrigger value="members">Members ({members.length})</TabsTrigger>
                    <TabsTrigger value="invitations">Pending Invitations ({invitations.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="members" className="mt-4">
                    <Card>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Member</TableHead>
                                        <TableHead>Role</TableHead>
                                        <TableHead>Joined</TableHead>
                                        {isAdmin && <TableHead className="w-12"></TableHead>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {members.map((member) => (
                                        <TableRow key={member.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="h-8 w-8">
                                                        <AvatarImage
                                                            src={member.user.avatarUrl ?? undefined}
                                                        />
                                                        <AvatarFallback>
                                                            {(member.user.name ?? member.user.email)
                                                                .charAt(0)
                                                                .toUpperCase()}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div>
                                                        <p className="font-medium">
                                                            {member.user.name ?? 'No name'}
                                                        </p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {member.user.email}
                                                        </p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="secondary">{member.role}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                {new Date(member.joinedAt).toLocaleDateString()}
                                            </TableCell>
                                            {isAdmin && (
                                                <TableCell>
                                                    {member.role !== 'owner' && (
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon">
                                                                    <MoreHorizontal className="h-4 w-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem
                                                                    className="text-destructive"
                                                                    onClick={() =>
                                                                        handleRemoveMember(
                                                                            member.user.id,
                                                                            member.user.name ??
                                                                                member.user.email
                                                                        )
                                                                    }
                                                                >
                                                                    <Trash2 className="mr-2 h-4 w-4" />
                                                                    Remove
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="invitations" className="mt-4">
                    <Card>
                        {invitationsLoading ? (
                            <CardContent className="py-8">
                                <Skeleton className="h-20 w-full" />
                            </CardContent>
                        ) : invitations.length === 0 ? (
                            <CardContent className="flex flex-col items-center py-8">
                                <Mail className="h-12 w-12 text-muted-foreground" />
                                <p className="mt-4 text-muted-foreground">No pending invitations</p>
                            </CardContent>
                        ) : (
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Role</TableHead>
                                            <TableHead>Invited By</TableHead>
                                            <TableHead>Expires</TableHead>
                                            {isAdmin && <TableHead className="w-12"></TableHead>}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {invitations.map((invitation) => (
                                            <TableRow key={invitation.id}>
                                                <TableCell>{invitation.email}</TableCell>
                                                <TableCell>
                                                    <Badge variant="secondary">{invitation.role}</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {invitation.invitedBy.name ?? 'Unknown'}
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(invitation.expiresAt).toLocaleDateString()}
                                                </TableCell>
                                                {isAdmin && (
                                                    <TableCell>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() =>
                                                                handleRevokeInvitation(invitation.id)
                                                            }
                                                        >
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        )}
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function InviteMemberDialog({ tenantId }: { tenantId: string }) {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<TMembershipRole>('member');
    const createInvitation = useCreateInvitation(tenantId);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!email.trim()) {
            toast.error('Email is required');
            return;
        }

        createInvitation.mutate(
            { email: email.trim(), role },
            {
                onSuccess: () => {
                    toast.success('Invitation sent successfully');
                    setOpen(false);
                    setEmail('');
                    setRole('member');
                },
                onError: (error) => {
                    toast.error(error.message || 'Failed to send invitation');
                },
            }
        );
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite Member
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Invite Team Member</DialogTitle>
                    <DialogDescription>Send an invitation to join your organization</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="colleague@example.com"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="role">Role</Label>
                            <Select value={role} onValueChange={(v) => setRole(v as TMembershipRole)}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                Admins can manage members and settings
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={createInvitation.isPending}>
                            {createInvitation.isPending ? 'Sending...' : 'Send Invitation'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function MembersSkeleton() {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <Skeleton className="h-8 w-40" />
                    <Skeleton className="mt-2 h-4 w-56" />
                </div>
                <Skeleton className="h-10 w-32" />
            </div>
            <Skeleton className="h-10 w-64" />
            <Card>
                <CardContent className="p-0">
                    <div className="space-y-4 p-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center gap-4">
                                <Skeleton className="h-10 w-10 rounded-full" />
                                <div className="flex-1">
                                    <Skeleton className="h-4 w-32" />
                                    <Skeleton className="mt-1 h-3 w-48" />
                                </div>
                                <Skeleton className="h-5 w-16" />
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
